import { useMemo, useState } from 'react'

import { AssetLogo } from '../components/AssetLogo'
import { Delta, KindBadge, SectionHeading, Skeleton, Stat } from '../components/ui'
import { useMarket } from '../hooks/queries'
import { compactNumber, dateOnly, usd } from '../lib/format'

type Sort = 'cap' | 'change' | 'price' | 'symbol'

export function MarketPage() {
  const { data, isLoading } = useMarket()
  const [sort, setSort] = useState<Sort>('cap')
  const [query, setQuery] = useState('')

  // Split rather than one combined count: `crypto` is priced off the general
  // market, not Robinhood Chain, and folding it into one number is exactly
  // the kind of thing this page exists to be precise about.
  const counts = useMemo(() => {
    if (!data) return { coin: 0, crypto: 0 }
    let coin = 0
    let crypto = 0
    for (const asset of data.assets) {
      if (asset.kind === 'coin') coin++
      else if (asset.kind === 'crypto') crypto++
    }
    return { coin, crypto }
  }, [data])

  const rows = useMemo(() => {
    if (!data) return []
    const needle = query.trim().toLowerCase()

    const list = data.assets.filter(
      (asset) =>
        !needle ||
        asset.symbol.toLowerCase().includes(needle) ||
        asset.name.toLowerCase().includes(needle),
    )

    return list.sort((a, b) => {
      if (sort === 'symbol') return a.symbol.localeCompare(b.symbol)
      if (sort === 'price') return b.price - a.price
      if (sort === 'change') return b.change24h - a.change24h
      return (b.marketCap ?? 0) - (a.marketCap ?? 0)
    })
  }, [data, query, sort])

  if (isLoading || !data) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  return (
    <div className="py-6">
      <SectionHeading
        title="The universe"
        sub="Every token a pack can draw from. Nothing outside this list can come out of a pack."
      />

      <div className="panel mb-5 flex flex-wrap items-end justify-between gap-6 p-5">
        <div className="flex flex-wrap gap-8">
          <Stat label="Robinhood Chain tokens" value={counts.coin} />
          <Stat label="Market crypto" value={counts.crypto} sub="BTC, SOL: not on Robinhood Chain" />
          <Stat
            label="Cap floor"
            value={compactNumber(data.minMarketCap)}
            sub="Below this, nothing is drawable"
          />
          <Stat label="Priced" value={dateOnly(data.refreshedAt)} sub={data.source ?? undefined} />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search ticker or name"
          className="panel-2 min-w-[200px] flex-1 px-3 py-2 text-sm outline-none"
          style={{ color: 'var(--color-text)' }}
        />

      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: 'var(--color-faint)' }}>
                <SortHeader label="Asset" active={sort === 'symbol'} onClick={() => setSort('symbol')} />
                <SortHeader label="Price" align="right" active={sort === 'price'} onClick={() => setSort('price')} />
                <SortHeader label="24h" align="right" active={sort === 'change'} onClick={() => setSort('change')} />
                <SortHeader label="Market cap" align="right" active={sort === 'cap'} onClick={() => setSort('cap')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((asset) => (
                <tr key={`${asset.kind}:${asset.symbol}`} className="border-t">
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <AssetLogo kind={asset.kind} symbol={asset.symbol} size={28} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{asset.symbol}</span>
                          <KindBadge kind={asset.kind} />
                        </div>
                        <div
                          className="truncate text-[0.6875rem]"
                          style={{ color: 'var(--color-faint)' }}
                        >
                          {asset.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="tnum px-3 py-2.5 text-right font-medium">{usd(asset.price)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Delta value={asset.change24h} />
                  </td>
                  <td
                    className="tnum px-5 py-2.5 text-right"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {compactNumber(asset.marketCap)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="px-5 py-10 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
            Nothing matches “{query}”.
          </div>
        )}
      </div>

      <p className="mt-4 max-w-2xl text-xs leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        Caps come straight from the data source. Cap affects how often a token is drawn, and how
        large a payout it is allowed to carry, never what a pull pays, which is always priced off
        the live unit price. Robinhood Chain carries tokenized equities, so familiar tickers appear
        here as tokens: those are the chain's own instruments, priced on the chain, not listed
        shares. A handful are the exception: BTC, SOL and the fully-backed gold and silver
        tokens, badged{' '}
        <span style={{ color: '#22d3ee' }}>Market</span> rather than{' '}
        <span style={{ color: 'var(--color-brand)' }}>RHC</span> because none of them is tokenized
        on Robinhood Chain at all; those are priced off the general market instead. The metals are
        redeemable claims on allocated bullion, not a commodity price feed.
      </p>
    </div>
  )
}

function SortHeader({
  label,
  active,
  onClick,
  align = 'left',
}: {
  label: string
  active: boolean
  onClick: () => void
  align?: 'left' | 'right'
}) {
  return (
    <th className={`px-5 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        onClick={onClick}
        className="text-[0.6875rem] font-semibold tracking-wider uppercase transition-colors"
        style={{ color: active ? 'var(--color-brand)' : 'inherit' }}
      >
        {label}
      </button>
    </th>
  )
}
